// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu / Categories  (shared by pages/Menu.tsx and pages/Categories.tsx)
// Glue between a list of catches and the pack-size + cut sheet: which catch's sheet is open, what the cart already
// holds for it, and how a confirmed sheet is written back to the cart. A page spreads `cardProps(item)` onto each
// <ProductCard> and `sheetProps` onto ONE <PackSheet>, so every Add / Edit button behaves the same everywhere.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";
import type { CutId } from "../data/cuts";
import { PACK_SIZES, type PackId } from "../data/packs";
import type { SeafoodItem } from "../data/seafood";
import useCart from "./useCart";
import useFavouriteCatches from "./useFavouriteCatches";

const EMPTY: Record<PackId, number> = { "500g": 0, "1kg": 0 };

export default function usePackSheet() {
  const { lines, qtyOf, setQty, cutIdOf } = useCart();
  const favourites = useFavouriteCatches();
  const [sheetItem, setSheetItem] = useState<SeafoodItem | null>(null);

  /** Packs of a catch already in the cart, by size. */
  const packsInCart = (id: string): Record<PackId, number> => ({ "500g": qtyOf(id, "500g"), "1kg": qtyOf(id, "1kg") });

  const confirm = (quantities: Record<PackId, number>, cut: CutId) => {
    if (sheetItem) for (const pack of PACK_SIZES) setQty(sheetItem.id, pack.id, quantities[pack.id], cut);
    setSheetItem(null);
  };

  return {
    /** Props for one catch's card. */
    cardProps: (item: SeafoodItem) => ({
      item,
      inCart: packsInCart(item.id),
      cut: lines.find((line) => line.item.id === item.id)?.cut,
      onOpen: () => setSheetItem(item),
      // A heart only for signed-in customers; saving from a card keeps the default cut and pack.
      favourite: favourites.available ? { saved: favourites.has(item.id), onToggle: () => void favourites.toggle(item.id) } : undefined,
    }),
    /** Props for the single sheet a page renders. */
    sheetProps: {
      item: sheetItem,
      inCart: sheetItem ? packsInCart(sheetItem.id) : EMPTY,
      cutInCart: sheetItem ? cutIdOf(sheetItem.id) : undefined,
      onClose: () => setSheetItem(null),
      onConfirm: confirm,
    },
  };
}
